/**
 * md-assignment-panel.tsx — Shows current MD assignment; edit button for staff:admin.
 *
 * AC 2: when no MD is assigned, escalation routes to exception queue with
 * escalation-md-unassigned tag (routing handled by Story 8.2 — this panel
 * provides the configuration UI).
 */

import type { JSX } from 'react';
import { useState } from 'react';

import type { MdAssignmentRecord } from './escalation-params-api.js';

interface MdAssignmentPanelProps {
  assignment: MdAssignmentRecord | null;
  onSave: (staffSub: string, staffName: string) => Promise<void>;
  saving: boolean;
  saveError: string | null;
}

export function MdAssignmentPanel({
  assignment,
  onSave,
  saving,
  saveError,
}: MdAssignmentPanelProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [staffSub, setStaffSub] = useState(assignment?.staffSub ?? '');
  const [staffName, setStaffName] = useState(assignment?.staffName ?? '');

  async function handleSave(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    await onSave(staffSub, staffName);
    setEditing(false);
  }

  return (
    <div className="border border-gray-200 rounded p-4">
      <h3 className="text-sm font-semibold text-gray-800 mb-3">Medical Director Assignment</h3>

      {!editing ? (
        <>
          {assignment ? (
            <div className="text-sm text-gray-700 space-y-1">
              <p>
                <span className="font-medium">Name:</span> {assignment.staffName}
              </p>
              <p>
                <span className="font-medium">Cognito Sub:</span>{' '}
                <span className="font-mono text-xs">{assignment.staffSub}</span>
              </p>
              <p className="text-xs text-gray-500">
                Assigned by {assignment.assignedBy} at{' '}
                {new Date(assignment.assignedAt).toLocaleString()}
              </p>
            </div>
          ) : (
            <p className="text-sm text-amber-600">
              No MD assigned. MD-tier escalations will route to the exception queue with tag
              &ldquo;escalation-md-unassigned&rdquo; until an MD is configured.
            </p>
          )}
          <button
            type="button"
            onClick={() => {
              setStaffSub(assignment?.staffSub ?? '');
              setStaffName(assignment?.staffName ?? '');
              setEditing(true);
            }}
            className="mt-3 text-sm text-blue-600 hover:text-blue-800"
          >
            {assignment ? 'Change MD Assignment' : 'Assign Medical Director'}
          </button>
        </>
      ) : (
        <form onSubmit={(e) => void handleSave(e)} className="space-y-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">MD Display Name</label>
            <input
              type="text"
              value={staffName}
              onChange={(e) => setStaffName(e.target.value)}
              required
              className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm"
              placeholder="e.g. Dr. Jane Smith"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">MD Cognito Sub (staff pool)</label>
            <input
              type="text"
              value={staffSub}
              onChange={(e) => setStaffSub(e.target.value)}
              required
              className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm font-mono"
              placeholder="Cognito sub UUID"
            />
          </div>
          {saveError && <p className="text-red-600 text-xs">{saveError}</p>}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={saving}
              className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Assignment'}
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
